import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import BulkEditModal from "../../components/admin/BulkEditModal";
import { api, money, formatApiError } from "../../lib/api";
import { thumbnailUrl } from "../../lib/imgSrcSet";
import {
  Plus, Search, Edit, Trash2, Copy, MoreHorizontal, ImageOff,
  ExternalLink, Archive, ArchiveRestore, SlidersHorizontal, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const FEATURE_DEFAULTS = { heated: false, blind_spot: false, auto_dimming: false, with_camera: false, hud: false, rain_sensor: false };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [shipClasses, setShipClasses] = useState([]);
  const [filterMissing, setFilterMissing] = useState(false);
  const navigate = useNavigate();

  const classMap = React.useMemo(
    () => Object.fromEntries((shipClasses || []).map((c) => [c.id, c.name])),
    [shipClasses]
  );

  const isMissingSetup = React.useCallback(
    (p) => !p.shipping_class_id || !classMap[p.shipping_class_id] || !(Number(p.weight_lbs) > 0),
    [classMap]
  );
  const missingCount = React.useMemo(() => products.filter(isMissingSetup).length, [products, isMissingSetup]);
  const visibleProducts = React.useMemo(
    () => (filterMissing ? products.filter(isMissingSetup) : products),
    [products, filterMissing, isMissingSetup]
  );

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/products${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setProducts(data.products);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    api.get("/admin/shipping-classes")
      .then(({ data }) => setShipClasses(data?.classes || (Array.isArray(data) ? data : [])))
      .catch(() => setShipClasses([]));
  }, []);

  const toggleSelect = (id) => setSelected((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allSelected = visibleProducts.length > 0 && visibleProducts.every((p) => selected.has(p.id));
  const toggleSelectAll = () => setSelected((s) => {
    const next = new Set(s);
    if (visibleProducts.length > 0 && visibleProducts.every((p) => s.has(p.id))) {
      visibleProducts.forEach((p) => next.delete(p.id));
    } else {
      visibleProducts.forEach((p) => next.add(p.id));
    }
    return next;
  });

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone. (Tip: use Archive to just hide it.)`)) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setActive = async (p, active) => {
    try {
      await api.put(`/admin/products/${p.id}`, { active });
      toast.success(active ? "Listing activated" : "Listing archived (hidden from store)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sellSimilar = (p) => {
    navigate("/admin/products/new", {
      state: {
        prefill: {
          name: `${p.name} (Copy)`,
          description: p.description || "",
          oem_number: p.oem_number || "",
          category: p.category,
          position: p.position,
          fitment_type: p.fitment_type,
          price: p.price,
          inventory_qty: 0,
          images: [...(p.images || [])],
          price_tiers: [...(p.price_tiers || [])],
          features: { ...FEATURE_DEFAULTS, ...(p.features || {}) },
          vehicle_ids: [...(p.vehicle_ids || [])],
        },
      },
    });
  };

  const bulkSetActive = async (active) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`${active ? "Activate" : "Archive"} ${ids.length} selected product${ids.length === 1 ? "" : "s"}?`)) return;
    try {
      const { data } = await api.post("/admin/products/bulk-update", { product_ids: ids, changes: { active }, dry_run: false });
      toast.success(`${active ? "Activated" : "Archived"} ${data.applied} product${data.applied === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`PERMANENTLY DELETE ${ids.length} product${ids.length === 1 ? "" : "s"}? This cannot be undone. Consider Archive instead.`)) return;
    if (!window.confirm(`Are you absolutely sure? ${ids.length} listing${ids.length === 1 ? "" : "s"} will be removed forever.`)) return;
    try {
      for (const id of ids) await api.delete(`/admin/products/${id}`);
      toast.success(`Deleted ${ids.length} product${ids.length === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout
      title="Products"
      actions={
        <Link to="/admin/products/new" data-testid="admin-products-new-btn" className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-4 h-10 text-sm tracking-wide rounded-sm">
          <Plus size={14} /> New product
        </Link>
      }
    >
      <div className="bg-white border border-zinc-200 rounded-md overflow-visible">
        {/* Search */}
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-3">
          <Search size={14} className="text-zinc-400" />
          <input
            data-testid="admin-products-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search SKU or name…"
            className="flex-1 text-sm outline-none bg-transparent"
          />
          <button onClick={load} className="text-xs px-3 py-1 border border-zinc-200 rounded hover:bg-zinc-50">Filter</button>
          <button
            onClick={() => setFilterMissing((v) => !v)}
            data-testid="products-missing-filter"
            title="Show only products missing a shipping class or weight"
            className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded border transition ${
              filterMissing
                ? "bg-amber-500 border-amber-500 text-white"
                : "bg-white border-amber-300 text-amber-700 hover:bg-amber-50"
            }`}
          >
            <AlertTriangle size={12} />
            Missing shipping setup:{" "}
            <span data-testid="products-missing-count" className="font-bold font-mono">{missingCount}</span>
          </button>
        </div>

        {/* Bulk actions toolbar */}
        {selected.size > 0 && (
          <div className="px-5 py-3 bg-zinc-900 text-white flex items-center gap-3 flex-wrap" data-testid="bulk-actions-toolbar">
            <span className="text-sm font-medium" data-testid="bulk-selected-count">{selected.size} selected</span>
            <button onClick={() => setBulkEditOpen(true)} data-testid="bulk-edit-open-btn" className="inline-flex items-center gap-2 text-xs bg-white/10 hover:bg-white/20 px-3 h-8 rounded-sm"><SlidersHorizontal size={13} /> Bulk edit</button>
            <button onClick={() => bulkSetActive(true)} data-testid="bulk-activate-btn" className="inline-flex items-center gap-2 text-xs bg-white/10 hover:bg-white/20 px-3 h-8 rounded-sm"><ArchiveRestore size={13} /> Activate</button>
            <button onClick={() => bulkSetActive(false)} data-testid="bulk-archive-btn" className="inline-flex items-center gap-2 text-xs bg-white/10 hover:bg-white/20 px-3 h-8 rounded-sm"><Archive size={13} /> Archive</button>
            <button onClick={bulkDelete} data-testid="bulk-delete-btn" className="inline-flex items-center gap-2 text-xs bg-red-600/80 hover:bg-red-600 px-3 h-8 rounded-sm"><Trash2 size={13} /> Delete</button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-white/70 hover:text-white ml-auto">Clear</button>
          </div>
        )}

        {/* Table */}
        <table className="w-full text-sm" data-testid="admin-products-table">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 bg-zinc-50">
            <tr>
              <th className="px-4 py-3 w-10">
                <input type="checkbox" data-testid="select-all-checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 align-middle" />
              </th>
              <th className="text-left px-3 py-3 w-16">Image</th>
              <th className="text-left px-3 py-3">Product</th>
              <th className="text-left px-3 py-3">SKU</th>
              <th className="text-left px-3 py-3">Category</th>
              <th className="text-left px-3 py-3">Position</th>
              <th className="text-left px-3 py-3">Shipping</th>
              <th className="text-right px-3 py-3">Price</th>
              <th className="text-right px-3 py-3">Stock</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="text-center py-10 text-zinc-500">Loading…</td></tr>}
            {!loading && visibleProducts.length === 0 && (
              <tr><td colSpan={11} className="text-center py-10 text-zinc-500">
                {filterMissing ? "🎉 No products are missing shipping setup." : "No products yet."}
              </td></tr>
            )}
            {visibleProducts.map((p) => (
              <tr key={p.id} className={`border-t border-zinc-100 hover:bg-zinc-50 ${selected.has(p.id) ? "bg-amber-50/60" : ""} ${p.active === false ? "opacity-60" : ""}`} data-testid={`admin-product-row-${p.sku}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" data-testid={`select-checkbox-${p.sku}`} checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} className="w-4 h-4 align-middle" />
                </td>
                <td className="px-3 py-3"><ProductThumb src={p.images?.[0]} name={p.name} /></td>
                <td className="px-3 py-3 max-w-xs">
                  <Link to={`/admin/products/${p.id}`} className="font-medium text-zinc-900 hover:text-red-600 line-clamp-1">{p.name}</Link>
                  <span className="inline-block mt-0.5 text-[10px] font-bold bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded font-mono">{p.fitment_count} fitment{p.fitment_count === 1 ? "" : "s"}</span>
                </td>
                <td className="px-3 py-3 font-mono text-xs text-zinc-600">{p.sku}</td>
                <td className="px-3 py-3 text-zinc-600">{p.category}</td>
                <td className="px-3 py-3 text-zinc-600">{p.position}</td>
                <td className="px-3 py-3"><ShippingCell p={p} className={classMap[p.shipping_class_id]} /></td>
                <td className="px-3 py-3 text-right font-mono">{money(p.price)}</td>
                <td className="px-3 py-3 text-right font-mono"><span className={p.inventory_qty < 3 ? "text-amber-600" : ""}>{p.inventory_qty}</span></td>
                <td className="px-3 py-3">
                  {p.active === false ? (
                    <span data-testid={`status-${p.sku}`} className="text-[10px] uppercase tracking-wide font-bold bg-zinc-200 text-zinc-600 px-2 py-1 rounded">Archived</span>
                  ) : (
                    <span data-testid={`status-${p.sku}`} className="text-[10px] uppercase tracking-wide font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Active</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <Link to={`/admin/products/${p.id}`} data-testid={`admin-product-edit-${p.sku}`} className="inline-flex items-center gap-1 px-2.5 h-8 text-xs border border-zinc-200 rounded hover:bg-zinc-100 text-zinc-700"><Edit size={13} /> Edit</Link>
                    <button onClick={() => sellSimilar(p)} data-testid={`sell-similar-${p.sku}`} className="inline-flex items-center gap-1 px-2.5 h-8 text-xs border border-zinc-200 rounded hover:bg-zinc-100 text-zinc-700"><Copy size={13} /> Sell similar</button>
                    <div className="relative">
                      <button onClick={() => setMenuOpenId(menuOpenId === p.id ? null : p.id)} data-testid={`more-menu-${p.sku}`} className="inline-flex items-center justify-center w-8 h-8 border border-zinc-200 rounded hover:bg-zinc-100 text-zinc-600"><MoreHorizontal size={15} /></button>
                      {menuOpenId === p.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setMenuOpenId(null)} />
                          <div className="absolute right-0 mt-1 w-48 bg-white border border-zinc-200 rounded-md shadow-lg z-20 py-1 text-left" data-testid={`more-panel-${p.sku}`}>
                            <a href={`/product/${p.slug}`} target="_blank" rel="noreferrer" onClick={() => setMenuOpenId(null)} className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50"><ExternalLink size={13} /> View product</a>
                            <button onClick={() => { setMenuOpenId(null); sellSimilar(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50"><Copy size={13} /> Sell similar</button>
                            <Link to={`/admin/products/${p.id}`} onClick={() => setMenuOpenId(null)} className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50"><Edit size={13} /> Edit</Link>
                            {p.active === false ? (
                              <button onClick={() => { setMenuOpenId(null); setActive(p, true); }} data-testid={`activate-${p.sku}`} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-700 hover:bg-emerald-50"><ArchiveRestore size={13} /> Activate listing</button>
                            ) : (
                              <button onClick={() => { setMenuOpenId(null); setActive(p, false); }} data-testid={`archive-${p.sku}`} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-700 hover:bg-zinc-50"><Archive size={13} /> Archive / disable</button>
                            )}
                            <div className="border-t border-zinc-100 my-1" />
                            <button onClick={() => { setMenuOpenId(null); handleDelete(p.id, p.name); }} data-testid={`admin-product-delete-${p.sku}`} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"><Trash2 size={13} /> Delete permanently</button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {bulkEditOpen && (
        <BulkEditModal
          productIds={[...selected]}
          onClose={() => setBulkEditOpen(false)}
          onApplied={() => { setBulkEditOpen(false); load(); }}
        />
      )}
    </AdminLayout>
  );
}

function ShippingCell({ p, className }) {
  const weight = Number(p.weight_lbs) || 0;
  const hasClass = !!p.shipping_class_id && !!className;
  const missing = !hasClass || weight <= 0;

  if (missing) {
    return (
      <span
        data-testid={`shipping-missing-${p.sku}`}
        title="Assign a shipping class and weight to enable accurate shipping quotes"
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded"
      >
        <AlertTriangle size={11} /> Missing setup
      </span>
    );
  }

  const weightLabel = `${weight % 1 === 0 ? weight : weight.toFixed(2)} ${weight === 1 ? "lb" : "lbs"}`;
  return (
    <div className="flex flex-col gap-1" data-testid={`shipping-cell-${p.sku}`}>
      <span className="text-xs text-zinc-700">
        <span className="font-medium">{className}</span>
        <span className="text-zinc-400"> · </span>
        <span className="font-mono">{weightLabel}</span>
      </span>
      {(p.freight_required || p.ships_free) && (
        <div className="flex flex-wrap gap-1">
          {p.freight_required && (
            <span className="text-[9px] uppercase tracking-wide font-bold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Freight</span>
          )}
          {p.ships_free && (
            <span className="text-[9px] uppercase tracking-wide font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Free</span>
          )}
        </div>
      )}
    </div>
  );
}

function ProductThumb({ src, name }) {
  const [err, setErr] = useState(false);
  const url = src ? thumbnailUrl(src) : null;
  if (!url || err) {
    return (
      <div className="w-12 h-12 rounded bg-zinc-100 border border-zinc-200 flex flex-col items-center justify-center text-zinc-400" data-testid="product-thumb-placeholder" title="No image">
        <ImageOff size={15} />
      </div>
    );
  }
  return <img src={url} alt={name} onError={() => setErr(true)} className="w-12 h-12 rounded object-contain bg-white border border-zinc-200 p-0.5" />;
}
